// eslint-disable-next-line no-restricted-imports
import { Menu } from '@workday/canvas-kit-react/menu';
import { createStyles, handleCsProp } from '@workday/canvas-kit-styling';
import { WcpStyles } from '@workday/wcp-tokens';
import React from 'react';
import { RoutedAnchor } from '../components/RoutedAnchor';

/** DevsiteMenu passes `menuIsOpen` to whatever it renders; it isn't a DOM attribute, so it's dropped here. */
interface MenuIsOpenProp {
  menuIsOpen?: boolean;
}

const ThemedMenuCard: React.FC<React.ComponentProps<typeof Menu.Card>> = (props) => (
  <Menu.Card
    {...props}
    backgroundColor={WcpStyles.color.bg.default}
    borderColor={WcpStyles.color.border.container}
    boxShadow={WcpStyles.depth[1]}
  />
);

const ThemedMenuItem = ({ menuIsOpen, ...elemProps }: React.ComponentProps<typeof Menu.Item> & MenuIsOpenProp) => (
  <Menu.Item {...handleCsProp(elemProps, themedMenuItemStyles)} />
);
ThemedMenuItem.Text = Menu.Item.Text;
ThemedMenuItem.Icon = Menu.Item.Icon;

// Built once at module level: calling `.as()` during render would create a new component type, remounting the item.
const MenuItemAsLink = Menu.Item.as(RoutedAnchor);

/**
 * A menu item that renders as the anchor itself rather than wrapping one, so that the whole padded
 * row is the link's hit area and CKR's roving focus lands somewhere Enter can navigate from. The
 * anchor is a `RoutedAnchor`, so in-app paths route client-side instead of reloading the site.
 */
const ThemedMenuItemLink = ({
  menuIsOpen,
  ...elemProps
}: React.ComponentProps<typeof MenuItemAsLink> & MenuIsOpenProp) => (
  <MenuItemAsLink {...handleCsProp(elemProps, themedMenuItemStyles)} />
);

const ThemedMenuList = (elemProps: React.ComponentProps<typeof Menu.List>) => (
  <Menu.List {...handleCsProp(elemProps, themedMenuListStyles)} />
);

const ThemedMenuDivider = (elemProps: React.ComponentProps<typeof Menu.Divider>) => (
  <Menu.Divider {...handleCsProp(elemProps, themedMenuDividerStyles)} />
);

export const ThemedMenu = (props: React.ComponentProps<typeof Menu>) => <Menu {...props} />;
ThemedMenu.Target = Menu.Target;
ThemedMenu.TargetContext = Menu.TargetContext;
ThemedMenu.Popper = Menu.Popper;
ThemedMenu.Card = ThemedMenuCard;
ThemedMenu.List = ThemedMenuList;
ThemedMenu.Item = ThemedMenuItem;
ThemedMenu.ItemLink = ThemedMenuItemLink;
ThemedMenu.Divider = ThemedMenuDivider;

const themedMenuItemStyles = createStyles({
  color: WcpStyles.color.text.default,
  backgroundColor: WcpStyles.color.bg.default,
  '.wd-icon-fill, .wd-icon-accent': {
    fill: WcpStyles.color.icon.default,
  },
  ':hover:not([aria-disabled="true"])': {
    color: WcpStyles.color.text.default,
    backgroundColor: WcpStyles.color.bg.alt.soft,
    '.wd-icon-fill, .wd-icon-accent': {
      fill: WcpStyles.color.icon.strong,
    },
  },
  ':focus:not([aria-disabled="true"])': {
    color: WcpStyles.color.text.inverse,
    backgroundColor: WcpStyles.color.bg.primary.default,
    '.wd-icon-fill, .wd-icon-accent': {
      fill: WcpStyles.color.icon.inverse,
    },
  },
  // Disabled items keep full-strength text and are dimmed solely by the global `a, button`
  // [aria-disabled] opacity rule in GlobalStyles — matching the newer Canvas Kit convention — rather
  // than CK14's much lighter fg.disabled token. Hover and the cursored state are listed explicitly so
  // the color never shifts as the pointer moves: CKR moves its cursor on mousedown, not hover, so
  // `:hover` and `:focus`/`.focus` are independent states (CKR stamps a literal `focus` class on the
  // cursored item alongside calling .focus()). `cursor` is restated because CKR's own disabled rule
  // (`cursor: default`) outranks the global rule. Revisit on a Canvas Kit upgrade — a newer version
  // may render disabled menu items correctly on its own, making this override unnecessary (or wrong).
  '&[aria-disabled="true"], &[aria-disabled="true"]:hover, &[aria-disabled="true"]:focus, &[aria-disabled="true"].focus':
    {
      color: WcpStyles.color.text.strong,
      backgroundColor: 'unset',
      cursor: 'not-allowed',
    },
});

const themedMenuListStyles = createStyles({
  backgroundColor: WcpStyles.color.bg.default,
});

const themedMenuDividerStyles = createStyles({
  borderColor: WcpStyles.color.border.divider,
});
